// Epic + connected child cards — the breakdown review/creation surface.
//
// A calm, scannable grid (design-workflow winner): one row per piece — a short
// NAME (inline-rename), a plain-language SCOPE line (`child.summary`), a size
// bucket, and depth one tap away ("Full wording & must-haves" = the unclamped
// scope line plus the criteria it owns; "Why these pieces?" = the rationale).
// The machinery a person doesn't decide on — the AC ids, the rationale
// paragraph, a re-listing confirm panel — stays OFF the resting surface. Trust
// is one green line right above the commit button.
//
// The name and the scope line are the ONLY model-authored bytes here, and both
// are bounded server-side (CHILD_TITLE_MAX / CHILD_SCOPE_MAX) so the resting
// row renders them whole — the confirm is a decision about text the user has
// actually read, not about an ellipsis. The USER-authored name goes through the
// same bound and the same single-line normalization on the server
// (`handleReadinessBreakdownOp`); this file's own clamp is a convenience, not
// the enforcement.
//
// Token-CSS on purpose (breakdown.css, `.bd-*`): the card sits natively inside
// BOTH `.loom-dashboard` (IssuePage) and `.loom-focus` (RefineFlow); the
// stylesheet bridges the two token systems, so there is one implementation.
//
// The card is a REPRESENTATION, never an authority:
//   - Confirm is token-gated (breakdownToken + the readiness acceptToken) and
//     sends ONE structured frame; the server reconstructs every write.
//   - Adjustments are TYPED ops (`readiness_breakdown_op`), not sentences. The
//     card still never mutates its own children — but rename / remove / merge /
//     move / add / park are now executed by the SERVER's pure reducer with no
//     agent turn at all, and every piece the user did not name is carried
//     forward byte-identical by construction. Only split-further, re-word and
//     free-text still reach a model, and then only into released slots.
//     `breakdownMessage.ts` remains the lane for a card the server could not
//     make addressable (an older proposal with no `childId`s).
//   - No file paths ever exist in the entry (privacy invariant, server-enforced).

import { useEffect, useRef, useState } from "react";

import {
  AC_HEADING,
  buildListAnswer,
  CHILD_TITLE_MAX,
} from "@loom/shared/readiness";
import { uiProse } from "@loom/shared/brand";

import { useT } from "../i18n";
import { useRemountSafeState } from "../state/remount-safe-state";
import { AutoGrowTextarea } from "../ui/AutoGrowTextarea";

import { AddChildDialog } from "./AddChildDialog";
import {
  buildAdjustMessage,
  buildMergeChildrenMessage,
  buildRemoveChildMessage,
  buildRenameChildMessage,
  buildSplitFurtherMessage,
} from "./breakdownMessage";
import { RemoveChildDialog, type RemoveDecision } from "./RemoveChildDialog";
import type { UseBreakdownCard } from "./useBreakdownCard";

import "./breakdown.css";

/**
 * Localized copy for an op refusal, keyed off the server's machine CODE.
 *
 * The server also sends a `message`, and rendering THAT would be a regression:
 * it is composed server-side in English and this UI is localized. An explicit
 * map (rather than a template-string key) keeps the lookup inside the typed
 * catalog, and an unrecognised code — a newer server against an older client —
 * falls back to the generic line instead of leaking English.
 */
const opErrorText = (
  t: ReturnType<typeof useT>,
  code: string | undefined,
): string => {
  switch (code) {
    case "orphan_criteria":
      return t("breakdown.op.err.orphan_criteria");
    case "empty_child":
      return t("breakdown.op.err.empty_child");
    case "child_count_out_of_bounds":
      return t("breakdown.op.err.child_count_out_of_bounds");
    case "parent_already_applied":
      return t("breakdown.op.err.parent_already_applied");
    case "coverage_gap":
      return t("breakdown.op.err.coverage_gap");
    case "breakdown_op_stale":
      return t("breakdown.op.err.breakdown_op_stale");
    case "unknown_child_id":
      return t("breakdown.op.err.unknown_child_id");
    case "breakdown_locked":
      return t("breakdown.op.err.breakdown_locked");
    case "criteria_write_refused":
      return t("breakdown.op.err.criteria_write_refused");
    case "child_not_addressable":
      return t("breakdown.op.err.child_not_addressable");
    case "no_sandbox":
      return t("breakdown.op.err.no_sandbox");
    case "merge_into_self":
      return t("breakdown.op.err.merge_into_self");
    case "criteria_moved":
      return t("breakdown.op.err.criteria_moved");
    case "ambiguous_parked_criterion":
      return t("breakdown.op.err.ambiguous_parked_criterion");
    case "unknown_parked_criterion":
      return t("breakdown.op.err.unknown_parked_criterion");
    case "child_id_collision":
      return t("breakdown.op.err.child_id_collision");
    default:
      return t("breakdown.op.failed");
  }
};

export interface BreakdownCardProps {
  readonly card: UseBreakdownCard;
  /** The parent issue's title, for the epic header row. */
  readonly epicTitle: string;
  /** Which issue this card belongs to. No message is composed from it any more
   *  (the keep-single sentence moved to the hosts, which own the lane it takes)
   *  — it rides the rendered section as `data-issue-iid`, so a surface showing
   *  two cards is still tellable apart from the outside. */
  readonly issueIid: number;
  /** The CURRENT readiness acceptToken — re-read from the latched readiness
   *  entry at render time, never cached (a latched token can be one card stale;
   *  the server re-verifies either way). */
  readonly acceptToken?: string;
  /** True while the agent is revising after an adjust (shimmer, no phase change). */
  readonly agentBusy?: boolean;
  /** True when the HOST already knows Confirm is blocked on a token park/unpark
   *  stripped on purpose and `runCardMethodology` hasn't re-minted
   *  yet — the host's `acceptToken` is the single source both this flag and
   *  this component's own `acceptToken` prop read, so this never contradicts
   *  it; it exists so the wait-state copy below can render off an explicit
   *  signal instead of this component re-deriving the same fact from a token's
   *  mere absence, which a proposal that simply never had one would also read
   *  as. All three hosts (both RefineFlow branches + IssuePage) pass it —
   *  they already compute exactly this to decide whether to spread
   *  `acceptToken`. Optional and OR'd with the internal check so a future host
   *  that forgets it still gets the honest message, just derived locally. */
  readonly confirmPending?: boolean;
  /** Send a self-labelled user turn (adjust / remove / rename / merge / keep-single). */
  readonly onSend: (text: string) => void;
  /**
   * The session is CLOSED and no frame this card sends will be answered — the
   * idle timer hard-closed it, or the server refused it. Distinct from
   * `agentBusy` (a turn is running) and from a missing token (a re-check is
   * pending): those are waits, this one never ends.
   *
   * Live, the "Session closed" banner rendered over a card whose every control
   * was still enabled — ⋯ menus, "+ Add a piece", "Adjust the split…", Confirm
   * and Decline — with the header reading "thinking…" and the caption promising
   * a re-check that could never complete. Pressing Confirm did nothing at all,
   * because the disabled predicate happened to catch it while nothing on screen
   * said why. Every mutating control reads this, and the commit caption names
   * it.
   */
  readonly sessionClosed?: boolean;
  /** Confirm the split (host wires sendBreakdownAccept + its deadline). */
  readonly onConfirm: () => void;
  /**
   * Keep this as one ticket.
   *
   * The card does NOT send the keep-single sentence itself. Which lane that
   * sentence takes is the HOST's knowledge: on the post-accept screen it has to
   * ride the `question_answer` frame that closes the split-time ask the agent
   * is blocked on (a plain send there is a second user turn, and the second
   * SIGTERM-kills the turn the first started), while the pre-accept host and
   * IssuePage send it as a plain self-labelled turn. Two buttons wired to two
   * different lanes was the live shape: the card's Decline and the question's
   * own "Keep as one ticket" sat on the same screen and did different things.
   */
  readonly onDecline: () => void;
  /** Navigate to a created child issue. */
  readonly onOpenChild?: (iid: number) => void;
  /** Remount-safe key scope (sessionId:iid:resetEpoch) for micro-state. */
  readonly stateKey: string;
  /** IssuePage collapses the done-state to a one-line banner and defers to the
   *  links panel; the focus flow keeps the full child list. */
  readonly collapseOnDone?: boolean;
  /** The host already renders this card's own name ("Proposed split") as the
   *  screen heading. Live that produced an `h1` "Proposed split" sitting
   *  directly on top of the kicker "PROPOSED SPLIT" — so the card drops the
   *  kicker and keeps the epic title, which is the part the heading does not
   *  say. */
  readonly headingInHost?: boolean;
  /** True once the parent card has been applied: its description is on GitLab
   *  and there is no post-apply must-have write lane, so taking work out of
   *  scope is refused here rather than half-done. Named honestly on the surface
   *  instead of surfacing later as a server deny. */
  readonly parkDisabled?: boolean;
}

export function BreakdownCard({
  card,
  epicTitle,
  issueIid,
  acceptToken,
  agentBusy,
  confirmPending,
  sessionClosed,
  onSend,
  onConfirm,
  onDecline,
  onOpenChild,
  stateKey,
  collapseOnDone,
  headingInHost,
  parkDisabled,
}: BreakdownCardProps) {
  const t = useT();
  const dead = sessionClosed === true;
  const [adjustDraft, setAdjustDraft] = useRemountSafeState<string>(
    `bd:adjust:${stateKey}`,
    "",
  );
  /**
   * The accept frame is out and the server has not answered yet.
   *
   * REMOUNT-SAFE for the same reason `armed` is, and it is the same defect one
   * step later: this was a plain `useState`, so the focus flow's ladder re-key
   * (any step/tail change, including the ones the confirm itself causes)
   * remounted the subtree and dropped the in-flight state. The row fell back to
   * the un-armed "Create N sub-issues" for the whole 5+ second write window,
   * with nothing on screen saying a write was running — a second press there is
   * a plausible double-commit on an action labelled "no undo".
   *
   * A timestamp rather than a boolean so the wall-clock backstop below can read
   * it; the server's own claim (`breakdown.consumedAtMs`) plus the
   * receipts-resume apply make a late duplicate idempotent, which is what makes
   * releasing after the backstop safer than a button that can wedge forever.
   */
  const [committingAt, setCommittingAt] = useRemountSafeState<number | null>(
    `bd:committing:${stateKey}`,
    null,
  );
  const committing = committingAt !== null;
  // In-place two-step commit: click 1 ARMS the row (Cancel takes the original
  // primary spot so a reflexive double-click can't fire the irreversible write);
  // click 2 sends. No separate panel — the pieces are already on screen.
  //
  // REMOUNT-SAFE, unlike every other micro-state's plain `useState`, because
  // this one spans two user gestures: the focus flow's ladder re-keys
  // `FocusFrame`'s body on any step/tail change, so an unrelated frame landing
  // between the two clicks remounted the subtree and silently ate the arm. The
  // user then pressed an enabled button repeatedly and no accept frame was ever
  // sent — a dead control with no explanation anywhere.
  const [armed, setArmed] = useRemountSafeState<boolean>(
    `bd:armed:${stateKey}`,
    false,
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  // On-demand disclosure — none of this is needed to decide split-vs-keep.
  const [expandedId, setExpandedId] = useState<string | null>(null); // one row's "Delivers"
  const [expandAll, setExpandAll] = useState(false); // "See what each delivers"
  const [showRationale, setShowRationale] = useState(false); // "Why these pieces?"
  const [showAdjust, setShowAdjust] = useState(false); // free-text box
  const [showAdd, setShowAdd] = useState(false); // structured "+ Add a piece"
  const [menuId, setMenuId] = useState<string | null>(null); // which row's "⋯" is open
  const [removingId, setRemovingId] = useState<string | null>(null); // remove dialog
  // Which piece a pending "Don't build this" attempt targets, ONLY when that
  // click's own client-side data (this child's OWN covered set, not the whole
  // reducer's postcondition sweep) proves it would empty exactly this row —
  // so an `empty_child` deny can name the piece honestly instead of guessing.
  // A deny from the Remove dialog can legitimately empty a DIFFERENT sibling
  // (parking a shared criterion strips every owner), so that path leaves this
  // unset and the generic wording — which claims nothing about which piece —
  // stays correct there too.
  const [emptyChildSelf, setEmptyChildSelf] = useState<{
    readonly title: string;
  } | null>(null);

  /**
   * This card has an outbound change the server has not answered yet.
   *
   * The ONLY state in which the rows on screen may be about to be replaced —
   * which is what the old session-global `agentBusy` veto was reaching for, one
   * scope too wide: `agentBusy` is true for ANY turn in the session, including
   * turns about a different issue entirely, and it wedged above zero whenever a
   * prompt ended without a `loom_stream_end`. Confirm was then permanently
   * disabled next to a caption promising the write.
   *
   * BOUNDED on purpose. A revision that never lands must not veto the commit
   * forever — the server refuses a stale confirm anyway (compare-and-swap on
   * `childrenHash` plus the breakdown-token HMAC), so the worst case of letting
   * one through is the existing `failed` screen with its stale hint, and that is
   * strictly better than a dead button.
   */
  const CONFIRM_VETO_MAX_MS = 90_000;
  const [pendingOp, setPendingOp] = useRemountSafeState<number | null>(
    `bd:pendingop:${stateKey}`,
    null,
  );
  const vetoed = pendingOp !== null;

  // A MOUNT is not an event. Both reset effects below fire on first render, and
  // two of the things they clear (`armed`, `pendingOp`) are remount-safe on
  // purpose — so an unguarded reset would wipe, on remount, exactly the state
  // that survives a remount. These refs make "changed since last render" the
  // trigger instead of "ran".
  const firstResetRef = useRef(true);
  const firstHashRef = useRef(true);

  const { progress: cardProgress, result: cardResult } = card;
  useEffect(() => {
    const first = firstResetRef.current;
    firstResetRef.current = false;
    setEditingId(null);
    setMenuId(null);
    setRemovingId(null);
    setShowAdd(false);
    // A MOUNT is not an answer — and `committingAt` is remount-safe precisely so
    // it survives one. The FIRST progress or result frame is the answer, and it
    // is also the point at which the phase leaves `review` and this footer stops
    // rendering at all; clearing here keeps the two consistent for the
    // `failed`-phase retry, which renders again.
    if (!first) {
      setArmed(false);
      setCommittingAt(null);
    }
  }, [cardProgress, cardResult]);

  // Wall-clock backstop for the commit, same shape and same argument as the op
  // veto below: a dropped `readiness_breakdown_result` must not leave the card
  // in a permanent "creating…" with no way forward. Re-sending the identical
  // accept is idempotent server-side (the breakdown carries `consumedAtMs`, and
  // the apply is receipts-resume), so re-offering the button is recoverable
  // where a wedged spinner is not.
  const COMMIT_INFLIGHT_MAX_MS = 90_000;
  useEffect(() => {
    if (committingAt === null) return;
    const remaining = committingAt + COMMIT_INFLIGHT_MAX_MS - Date.now();
    if (remaining <= 0) {
      setCommittingAt(null);
      return;
    }
    const timer = setTimeout(() => setCommittingAt(null), remaining);
    return () => clearTimeout(timer);
  }, [committingAt]);

  // An op RESULT is an answer, `ok` or not — either way this card's outbound
  // change is settled and the commit must be reachable again.
  const opResultEntry = card.opResult;
  useEffect(() => {
    if (opResultEntry !== null) setPendingOp(null);
    // `setPendingOp` is a stable useCallback; excluded so clearing can't re-arm.
  }, [opResultEntry]);

  // Wall-clock backstop: a dropped `readiness_breakdown_op_result` frame would
  // otherwise leave the veto latched for the rest of the flow. There is no
  // other release for that case today.
  useEffect(() => {
    if (pendingOp === null) return;
    const remaining = pendingOp + CONFIRM_VETO_MAX_MS - Date.now();
    if (remaining <= 0) {
      setPendingOp(null);
      return;
    }
    const timer = setTimeout(() => setPendingOp(null), remaining);
    return () => clearTimeout(timer);
  }, [pendingOp]);

  // A NEW proposal supersedes every open micro-state on this card. Keyed on the
  // entry's `childrenHash` — the same value a typed op sends as its
  // compare-and-swap — so a menu, a rename box or a remove dialog can never
  // outlive the rows it was opened against. The entry is replaced in place, so
  // the component does not remount and none of this resets on its own.
  const entryHash = card.entry?.childrenHash;
  useEffect(() => {
    const first = firstHashRef.current;
    firstHashRef.current = false;
    setEditingId(null);
    setMenuId(null);
    setRemovingId(null);
    setShowAdd(false);
    setEmptyChildSelf(null);
    if (first) return; // a remount is not a new proposal
    setArmed(false);
    // A new proposal IS the answer to whatever was outstanding — and it is the
    // precise event the old `agentBusy` veto was proxying for.
    setPendingOp(null);
  }, [entryHash]);

  // A revising turn (or a new proposal) supersedes an armed commit — never let
  // the user confirm a split that's mid-rotation (the F7 stale-token trigger).
  // Still keyed on the session-global flag: disarming a HOT commit on any turn
  // is cheap and correct, whereas gating `confirmable` on it was not.
  useEffect(() => {
    if (agentBusy === true) setArmed(false);
  }, [agentBusy]);

  // A session that closes under a HOT commit disarms it. The armed row is a
  // promise that the second press writes; it cannot keep that promise here.
  useEffect(() => {
    if (sessionClosed === true) setArmed(false);
  }, [sessionClosed]);

  /**
   * …and it closes every editor that is ALREADY OPEN.
   *
   * The render gates below fixed half of this: a control can no longer appear on
   * a card that cannot mutate. This is the state half. `editingId`, `showAdd`,
   * `showAdjust`, `menuId` and `removingId` are plain client state, so without
   * this they merely go INVISIBLE while the session is dead and spring back —
   * mid-word, against rows that may have moved — the moment a transient
   * condition (a commit backstop, a phase change) lifts.
   *
   * Keyed on the facts that END this card, not on the whole mutation gate:
   * `vetoed` is deliberately excluded. A veto is a wait of at most 90s, and it
   * is very often a DENY coming back — keeping the user's half-typed rename
   * through it is the point, not a leak.
   */
  const cardOver = dead || committing || card.phase !== "review";
  useEffect(() => {
    if (!cardOver) return;
    setEditingId(null);
    setMenuId(null);
    setRemovingId(null);
    setShowAdd(false);
    setShowAdjust(false);
  }, [cardOver]);

  // The armed state auto-reverts so it can't sit hot indefinitely. 4.5s was
  // short enough that a user who glanced away lost the arm and read the result
  // as a no-op, so the window is long enough to look away and back.
  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 8000);
    return () => clearTimeout(timer);
  }, [armed]);

  const entry = card.entry;
  if (entry === null || entry.noSplit) return null;

  const { phase, progress, result, sizing } = card;
  const n = entry.children.length;
  const totalCriteria = entry.criteria.length;
  const coveredCriteria = new Set(
    entry.children.flatMap((c) => [...c.coveredCriteria]),
  ).size;
  const fullyCovered = coveredCriteria === totalCriteria;
  // "Nothing left out" is a lie the moment something has been parked: the
  // badge is computed over `entry.criteria`, which already EXCLUDES parked
  // rows, so "fully covered" only ever means "every REMAINING must-have has a
  // piece" — never "nothing was dropped". The parked list sits right above
  // this footer making the drop visible, so the two can't both be honest
  // unless the badge says "remaining", not "nothing".
  const hasParked = (entry.parked?.length ?? 0) > 0;
  // Missing either token is not the same "no" as a real coverage gap: a park
  // or unpark deliberately strips both while the methodology re-runs, so a
  // fully-covered, non-busy card sitting here means the re-mint just hasn't
  // landed yet, not that anything is wrong. Surfaced as its own honest wait
  // state below rather than a disabled button with no explanation.
  // A CLOSED session outranks it: the re-mint rides a turn that will never run,
  // so "one moment, Loom is re-checking this split" is a promise about a dead
  // socket. That exact caption sat under a dead button live.
  const tokenPending =
    phase === "review" &&
    !dead &&
    !committing &&
    !vetoed &&
    fullyCovered &&
    (confirmPending === true ||
      entry.breakdownToken === undefined ||
      acceptToken === undefined);
  // THE single gate. Both Confirm buttons read it for `disabled` AND `commit`
  // re-reads this exact binding before it calls `onConfirm` — one predicate,
  // two readers, so an enabled Confirm that sends nothing is not expressible.
  // A second flag mirroring this could drift; this cannot.
  const confirmable =
    phase === "review" &&
    !dead &&
    !vetoed &&
    fullyCovered &&
    entry.breakdownToken !== undefined &&
    acceptToken !== undefined &&
    !committing;

  /** The irreversible write. The only caller of `onConfirm` on this card. */
  const commit = () => {
    if (!confirmable) return;
    setCommittingAt(Date.now());
    setArmed(false);
    onConfirm();
  };

  /** The `failed`-phase resume. Same one-binding rule as `commit`. */
  const retryable = acceptToken !== undefined && !committing && !dead;
  const retry = () => {
    if (!retryable) return;
    setCommittingAt(Date.now());
    onConfirm();
  };

  /** What the commit button will (or cannot yet) do — rendered as the caption
   *  AND as both buttons' `title`, so the two can never disagree. Ordered by
   *  which fact outranks which: a dead session is not a wait, so it is read
   *  before every "one moment" below it. */
  const commitCaption = dead
    ? t("breakdown.confirm.sessionClosed")
    : committing
      ? t("breakdown.committing.caption")
      : vetoed
        ? t("breakdown.confirm.opPending")
        : tokenPending
          ? t("breakdown.confirm.tokenPending")
          : t("breakdown.commitCaption", { count: String(n) });

  const childStatus = (
    localId: string,
  ): { state: "proposed" | "creating" | "created" | "linked"; iid?: number } => {
    const created = progress?.createdByLocalId.get(localId);
    const resultChild = result?.children?.find((c) => c.localId === localId);
    const iid = resultChild?.iid ?? created?.iid;
    if (iid === undefined) {
      return phase === "creating" ? { state: "creating" } : { state: "proposed" };
    }
    const linked = progress?.linkedLocalIds.has(localId) ?? phase === "done";
    return { state: linked ? "linked" : "created", iid };
  };

  const createdCount =
    result?.children?.length ?? progress?.createdByLocalId.size ?? 0;

  // TYPED LANE vs PROSE LANE. `opsAvailable` is false only for a proposal the
  // server could not make addressable — one persisted before stable ids
  // existed, or echoed by an older bridge image. Everything below picks the
  // typed op when it can and falls back to the self-labelled sentence when it
  // cannot, so a mid-deploy card degrades to exactly the previous behaviour
  // instead of offering buttons whose every press would deny.
  const ops = card.opsAvailable;

  // `+ Add a piece` moves an existing must-have into a brand-new piece — it can
  // never invent one. So the op is only ever possible when some sibling holds
  // TWO OR MORE must-haves; when every piece already holds exactly one, every
  // possible pick would strand its current owner (`AddChildDialog`'s own
  // `stranded` check), and the button was live anyway: fill both fields, tick a
  // box, and only then learn `Add the piece` was never going to enable. This is
  // provable up front from the rows already on screen, so the button itself is
  // gated rather than leaving the user to discover it via the dialog's guard.
  // Prose-lane fallback (`!ops`) is untouched — a free-text instruction can
  // restructure the whole split, so this structural rule doesn't apply to it.
  const addPieceImpossible =
    ops === true &&
    !entry.children.some((c) => c.coveredCriteria.length >= 2);

  const isReview = phase === "review";

  /**
   * THE MUTATION GATE. One binding, every control that can change the partition
   * — the rename editor, the row menu, the per-criterion drop, the Remove dialog,
   * "+ Add a piece" and its form, the free-text adjust box.
   *
   * It was four copies of the same conjunction before, and the copies had
   * already drifted in exactly the way that matters: an editor ALREADY OPEN
   * when the session died stayed open, because `isEditing` and `showAdd` are
   * client state and the gate was only ever consulted at the point that OPENS
   * them. A rename box with a live save button over a session nothing can answer is the
   * same defect as the enabled Confirm this whole round is closing — the
   * user's next act is swallowed and nothing says why.
   *
   * THREE halves, and all three are needed: the render sites below read it (so a
   * control cannot appear), the `cardOver` effect above closes whatever is
   * already open (so a control cannot survive the transition), and every send
   * path re-reads it (so a control that is mid-gesture when the gate shuts
   * cannot still put a frame on the wire).
   *
   * The free-text adjust box is the reason the gate is declared HERE, above the
   * senders, rather than next to the render sites: it was gated on `dead ||
   * committing` alone, so while `vetoed` — an op sent, no answer yet — it stayed
   * open as the ONLY live control on the card and sent a second
   * `readiness_breakdown_op` carrying the same `baseChildrenHash`. The server's
   * compare-and-swap passes (the model has not answered, so the hash has not
   * moved), `openBreakdownRevision` overwrites the open ticket with no receipt,
   * and the first request — the split-further the user actually asked for — is
   * dropped while its turn is SIGTERM-killed by the second.
   */
  const mutable = isReview && !vetoed && !dead && !committing;

  /** Mark this card as awaiting the server's answer to a change we just sent. */
  const markPending = () => setPendingOp(Date.now());

  /**
   * EVERY typed op leaves through here, so nothing can mutate the partition
   * without arming the commit veto. A send that never left (no `childrenHash`
   * — a proposal that predates typed ops) arms nothing: there is no answer
   * coming, so a veto would only wedge the button.
   */
  const sendOp: UseBreakdownCard["sendOp"] = (op, groundingText) => {
    const delivered = card.sendOp(op, groundingText);
    if (delivered) markPending();
    return delivered;
  };

  const commitRename = (ordinal: number, title: string) => {
    setEmptyChildSelf(null);
    // Collapse ALL whitespace, not just the ends. A GitLab issue title is
    // single-line by contract, and Loom composes two markdown structures out of
    // it: the parent's `- [ ] #12 — {title}` task-list row and every sibling's
    // `- AC2 — piece 3, "{title}"` build note. An embedded newline splits both
    // rows, so the corruption is Loom's own regardless of what GitLab does with
    // the title itself. `.trim()` never touches interior whitespace.
    const next = title.replace(/\s+/gu, " ").trim().slice(0, CHILD_TITLE_MAX);
    // An empty name is not a rename, and CLOSING the editor on one was the
    // whole defect: the save button stayed enabled, the click sent nothing, the
    // box shut and the old name reappeared — indistinguishable from a rename
    // that silently reverted. The editor stays open (the save button is
    // disabled and says why) so the user is looking at the field they still
    // have to fill.
    if (next.length === 0) return;
    const child = entry.children[ordinal - 1];
    const prev = child?.title.trim() ?? "";
    if (next !== prev) {
      if (ops && child?.childId !== undefined) {
        sendOp({ op: "rename", childId: child.childId, title: next });
      } else {
        onSend(buildRenameChildMessage(ordinal, next));
        markPending();
      }
    }
    setEditingId(null);
  };

  const send = (text: string) => {
    onSend(text);
    // The prose lane changes the partition too — an unaddressable card (older
    // proposal, older bridge image) must get the same commit veto, or the
    // degrade path is the one place a superseded confirm still slips through.
    markPending();
    setMenuId(null);
  };

  /** Criteria a piece covers that no OTHER piece covers — the ones that need a
   *  destination before it can be removed. Computed from data already on
   *  screen, so the dialog needs no extra round trip. */
  const uniqueCriteriaOf = (
    childId: string,
  ): { index: number; text: string }[] => {
    const self = entry.children.find((c) => c.childId === childId);
    if (!self) return [];
    return [...self.coveredCriteria]
      .filter(
        (i) =>
          !entry.children.some(
            (o) => o.childId !== childId && o.coveredCriteria.includes(i),
          ),
      )
      .map((i) => ({ index: i, text: entry.criteria[i] ?? "" }));
  };

  /** The must-have list AFTER a set of park decisions, in the canonical send
   *  format — the anchor that says the user made this call at the split step. A
   *  criteria move with no grounding text is refused. The bytes the server
   *  actually echoes and writes are its OWN render of the reducer's result; for
   *  a pure park the two are identical (both filter), and for a restore only the
   *  server knows the position the row returns to. */
  const groundingFor = (parkedIndices: ReadonlySet<number>): string =>
    buildListAnswer(
      AC_HEADING,
      entry.criteria.filter((_, i) => !parkedIndices.has(i)),
    );

  /**
   * Free text still reaches a model — it is an instruction that has to be
   * interpreted. But on the typed lane it goes as a `free_text` OP, which opens
   * a revision ticket with NO released slots: the model may name operations,
   * and while the ticket is open it may not answer by restating the whole
   * partition. So even here, pieces it does not name keep their exact wording.
   */
  const sendAdjust = () => {
    setEmptyChildSelf(null);
    // The gate, re-read at the send. The render gate below hides this box the
    // moment the card stops being mutable, but a Cmd+Enter already in flight
    // when a veto latches would otherwise still leave — and a second op against
    // an unmoved `baseChildrenHash` is precisely the one the server cannot
    // refuse.
    if (!mutable) return;
    const text = adjustDraft.trim();
    if (text.length === 0) return;
    const delivered = ops
      ? sendOp({ op: "free_text", instruction: text })
      : (onSend(buildAdjustMessage(text)), true);
    // Only clear what actually left. A dropped socket used to eat the sentence
    // the user had just typed, with nothing to retype from.
    if (delivered) {
      setAdjustDraft("");
      setShowAdjust(false);
    }
  };

  const confirmRemove = (childId: string, decisions: ReadonlyArray<RemoveDecision>) => {
    // This path's own `empty_child` (if any) empties a SIBLING, never the
    // removed piece itself — the generic wording is already correct for it.
    setEmptyChildSelf(null);
    const parked = new Set(
      decisions.filter((d) => d.to.kind === "park").map((d) => d.criterionIndex),
    );
    sendOp(
      {
        op: "remove",
        childId,
        disposition: decisions.map((d) => ({
          criterionId: `AC${String(d.criterionIndex + 1)}`,
          to: d.to,
        })),
      },
      // Only when scope actually leaves the ticket: an op that just moves work
      // between pieces changes no verified green and needs no grounding turn.
      parked.size > 0 ? groundingFor(parked) : undefined,
    );
    setRemovingId(null);
    setMenuId(null);
  };

  // Done + collapse (dashboard host): a one-line receipt; the GitLab-derived
  // Sub-issues links panel is the authoritative view from here on.
  if (phase === "done" && collapseOnDone === true) {
    return (
      <section className="bd-card is-collapsed" data-state="done">
        <span className="bd-epic-glyph" aria-hidden />
        <span className="bd-collapsed-text">
          {t("breakdown.done.title", { count: String(createdCount) })}
          {" — "}
          {t("breakdown.done.seeLinks")}
        </span>
      </section>
    );
  }

  return (
    <section
      className="bd-card"
      data-state={phase}
      data-issue-iid={String(issueIid)}
    >
      <header className="bd-head">
        <span className="bd-epic-glyph" aria-hidden />
        {/* Dropped when the host's own screen heading already says it — an `h1`
            "Proposed split" directly above a kicker "PROPOSED SPLIT" is the
            same words twice, and the epic title below is the half that carries
            information. */}
        {headingInHost !== true && (
          <span className="bd-kicker">{t("breakdown.review.title")}</span>
        )}
        <h3 className="bd-head-title">{epicTitle}</h3>
        {sizing !== null && (
          <span
            className="bd-chip bd-chip-size"
            title={t("breakdown.sizing.files", {
              count: String(sizing.overall.filesTouched),
            })}
          >
            {sizing.overall.locBucket}
          </span>
        )}
        {phase === "creating" && (
          <span className="bd-chip is-busy">
            {t("breakdown.creating", {
              done: String(createdCount),
              total: String(n),
            })}
          </span>
        )}
        {/* Keyed on THIS card's outstanding change, never on the session-global
            turn flag: "Revising the split…" over a session busy with anything
            else asserts a fact that is not true, and a wedged busy counter made
            it permanent. */}
        {isReview && !dead && vetoed && (
          <span className="bd-chip is-busy">{t("breakdown.review.revising")}</span>
        )}
        {/* The commit is out and the write is running. The `creating` chip above
            only appears once the FIRST progress frame lands, which is exactly
            the window this card had nothing to show. */}
        {isReview && !dead && committing && (
          <span className="bd-chip is-busy">{t("breakdown.committing")}</span>
        )}
        {/* Outranks both: neither of the above can still resolve. */}
        {isReview && dead && (
          <span className="bd-chip is-dead">
            {t("breakdown.sessionClosed.chip")}
          </span>
        )}
      </header>

      <ol className="bd-tree">
        {entry.children.map((child, i) => {
          const ordinal = i + 1;
          const status = childStatus(child.localId);
          const clickable = status.iid !== undefined && onOpenChild;
          // Editable unless THIS card has an unanswered change. Same reason as
          // the chip: a session-global flag froze the rows for turns that had
          // nothing to do with this partition. A closed session and an in-flight
          // commit both end editing too — the first because nothing would ever
          // answer, the second because the rows are being written right now.
          const editable = mutable;
          // Reads the gate, NOT just the id. The rename box is the one control
          // that renders off client state alone, so `editingId === localId` was
          // enough to keep an editor — with a live save button — open over a session the
          // idle timer had already closed. Every other control on this row was
          // behind `editable`; this one asked only whether it had been opened.
          const isEditing = editable && editingId === child.localId;
          const open = expandAll || expandedId === child.localId;
          const menuOpen = menuId === child.localId;
          const mergeTargets = entry.children
            .map((_, j) => j + 1)
            .filter((o) => o !== ordinal);
          return (
            <li key={child.localId} className={`bd-row is-${status.state}`}>
              <span className="bd-dot" aria-hidden />
              <span className="bd-ord">{ordinal}</span>
              <div className="bd-row-body">
                <div className="bd-row-head">
                  {isEditing ? (
                    // DELIBERATE, DOCUMENTED exception to the house rule that a
                    // multiline field commits on Ctrl/Cmd+Enter and plain Enter
                    // inserts a newline. A GitLab issue title is single-line BY
                    // CONTRACT, so this was never a multiline field: as a
                    // textarea, plain Enter put a newline into the draft and the
                    // invisible onBlur commit (type, Enter, click away) shipped
                    // it — splitting the parent's task-list row and every
                    // sibling's build-note row. An `<input>` cannot hold one, so
                    // plain Enter commits and the commit is a VISIBLE button
                    // rather than a blur nobody can see. `bd-adjust-input` below
                    // is genuinely prose and keeps the convention.
                    <span className="bd-name-editbox">
                      <input
                        type="text"
                        className="bd-name-edit"
                        autoFocus
                        value={editDraft}
                        maxLength={CHILD_TITLE_MAX}
                        aria-label={t("breakdown.child.rename")}
                        onChange={(e) => setEditDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitRename(ordinal, editDraft);
                          } else if (e.key === "Escape") setEditingId(null);
                        }}
                      />
                      <button
                        type="button"
                        className="bd-name-edit-ok"
                        aria-label={t("breakdown.child.renameSave")}
                        title={
                          editDraft.trim() === ""
                            ? t("breakdown.child.renameEmpty")
                            : t("breakdown.child.renameSave")
                        }
                        disabled={editDraft.trim() === ""}
                        onClick={() => commitRename(ordinal, editDraft)}
                      >
                        ✓
                      </button>
                    </span>
                  ) : clickable ? (
                    <button
                      type="button"
                      className="bd-name is-link"
                      onClick={() => onOpenChild(status.iid as number)}
                    >
                      #{String(status.iid)} {child.title}
                    </button>
                  ) : editable ? (
                    <button
                      type="button"
                      className="bd-name is-editable"
                      title={t("breakdown.child.rename")}
                      onClick={() => {
                        setEditDraft(child.title);
                        setEditingId(child.localId);
                      }}
                    >
                      {child.title}
                    </button>
                  ) : (
                    <span className="bd-name">{child.title}</span>
                  )}
                  <span className="bd-chip bd-chip-est">
                    {child.estimate.locBucket}
                  </span>
                  {editable && (
                    <button
                      type="button"
                      className="bd-row-menu-btn"
                      aria-label={t("breakdown.child.menu")}
                      aria-expanded={menuOpen}
                      onClick={() => setMenuId(menuOpen ? null : child.localId)}
                    >
                      ⋯
                    </button>
                  )}
                </div>
                {isEditing && editDraft.trim() === "" && (
                  <p className="bd-name-hint" role="status">
                    {t("breakdown.child.renameEmpty")}
                  </p>
                )}
                {/* The scope line and the title are the only two strings on
                    this card the user has not already read somewhere else —
                    the rows the disclosure reveals are the server's verbatim
                    split of their own verified criteria. Both are bounded
                    server-side so the resting row shows them whole; `is-open`
                    lifts the clamp for the tail case. Confirm authorises these
                    bytes. */}
                {child.summary.trim() !== "" && (
                  <p className={`bd-scope${open ? " is-open" : ""}`}>
                    {child.summary}
                  </p>
                )}
                {/* The build note, under the scope line and inside the same
                    disclosure. Same clamp treatment for the same reason: the
                    server bounds it so the resting row shows it whole, and
                    Confirm authorises these bytes too. Not editable here — the
                    piece's "adjust" flow is what re-opens it, so there is no
                    second, weaker path to changing what gets written. */}
                {child.approach !== undefined &&
                  child.approach.trim() !== "" && (
                    <p className={`bd-scope bd-approach${open ? " is-open" : ""}`}>
                      <span className="bd-approach-label">
                        {t("breakdown.child.approach")}
                      </span>{" "}
                      {child.approach}
                    </p>
                  )}
                {child.coveredCriteria.length > 0 && !expandAll && (
                  <button
                    type="button"
                    className="bd-delivers-toggle"
                    aria-expanded={open}
                    onClick={() =>
                      setExpandedId(open ? null : child.localId)
                    }
                  >
                    {t("breakdown.child.delivers")}{" "}
                    <span aria-hidden>{open ? "▾" : "▸"}</span>
                  </button>
                )}
                {open && child.coveredCriteria.length > 0 && (
                  <ul className="bd-delivers">
                    {child.coveredCriteria.map((index) => (
                      <li key={index}>
                        <span>{entry.criteria[index] ?? ""}</span>
                        {/* Retiring a must-have DIRECTLY. Until this existed the
                            only way to take work out of scope was to remove a
                            whole piece — and Remove is gated on n > 2, so a
                            two-piece split, the commonest shape there is, had no
                            scope-drop path at all. The op it sends has been on
                            the wire since day one with no sender. */}
                        {editable && !parkDisabled && ops && (
                          <button
                            type="button"
                            className="bd-crit-drop"
                            title={t("breakdown.criterion.drop")}
                            onClick={() => {
                              const kept = entry.criteria.filter(
                                (_, i) => i !== index,
                              );
                              if (kept.length === 0) return;
                              // This click's own must-have list is the ONLY
                              // one that matters here: if it holds just the
                              // one being dropped, THIS row is the piece an
                              // `empty_child` deny would be about — no need
                              // to wait for the server to say so.
                              setEmptyChildSelf(
                                child.coveredCriteria.length === 1
                                  ? { title: child.title }
                                  : null,
                              );
                              sendOp(
                                {
                                  op: "park_criteria",
                                  criterionIds: [`AC${String(index + 1)}`],
                                },
                                buildListAnswer(AC_HEADING, kept),
                              );
                            }}
                          >
                            {t("breakdown.criterion.drop")}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {removingId !== null &&
                  removingId === child.childId &&
                  editable && (
                    <RemoveChildDialog
                      childTitle={child.title}
                      orphans={uniqueCriteriaOf(removingId)}
                      siblings={entry.children
                        .filter(
                          (c): c is typeof c & { childId: string } =>
                            c.childId !== undefined && c.childId !== removingId,
                        )
                        .map((c) => ({
                          childId: c.childId,
                          title: c.title,
                          coveredCriteria: c.coveredCriteria,
                        }))}
                      parkDisabled={parkDisabled === true}
                      onCancel={() => setRemovingId(null)}
                      onConfirm={(decisions) =>
                        confirmRemove(removingId, decisions)
                      }
                    />
                  )}
                {menuOpen && editable && (
                  <div className="bd-row-menu" role="group">
                    <button
                      type="button"
                      onClick={() => {
                        setEditDraft(child.title);
                        setEditingId(child.localId);
                        setMenuId(null);
                      }}
                    >
                      {t("breakdown.child.rename")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // Genuinely needs new prose, so this one DOES reach the
                        // model — but through a revision ticket that releases
                        // only this childId, and with the replace-whole leg
                        // refused while it is open.
                        if (ops && child.childId !== undefined) {
                          sendOp({
                            op: "split_further",
                            childId: child.childId,
                          });
                          setMenuId(null);
                        } else {
                          send(buildSplitFurtherMessage(ordinal));
                        }
                      }}
                    >
                      {t("breakdown.child.splitFurther")}
                    </button>
                    {/* Merge / Remove reduce the piece count — only offered when
                        there are >2, so the split can't drop below the min of 2. */}
                    {n > 2 && (
                      <>
                        <label className="bd-menu-merge">
                          <span className="bd-sr">
                            {t("breakdown.child.mergeLabel")}
                          </span>
                          <select
                            value=""
                            aria-label={t("breakdown.child.mergeLabel")}
                            onChange={(e) => {
                              const into = Number(e.target.value);
                              if (into <= 0) return;
                              const target = entry.children[into - 1];
                              if (
                                ops &&
                                child.childId !== undefined &&
                                target?.childId !== undefined
                              ) {
                                sendOp({
                                  op: "merge",
                                  fromChildId: child.childId,
                                  intoChildId: target.childId,
                                });
                                setMenuId(null);
                              } else {
                                send(buildMergeChildrenMessage(ordinal, into));
                              }
                            }}
                          >
                            <option value="">{t("breakdown.child.merge")}</option>
                            {mergeTargets.map((o) => (
                              <option key={o} value={o}>
                                {t("breakdown.child.mergeInto", { n: String(o) })}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          className="is-danger"
                          onClick={() => {
                            // The question the old sentence answered for the
                            // user is now asked. Prose fallback keeps the old
                            // (redistributing) behaviour for an unaddressable card.
                            if (ops && child.childId !== undefined) {
                              setRemovingId(child.childId);
                              setMenuId(null);
                            } else {
                              send(buildRemoveChildMessage(ordinal));
                            }
                          }}
                        >
                          {t("breakdown.child.remove")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {status.state === "creating" && (
                <span className="bd-row-status">
                  {t("breakdown.creating.inflight")}…
                </span>
              )}
              {phase === "creating" && status.state === "proposed" && (
                <span className="bd-row-status is-muted">
                  {t("breakdown.creating.queued")}
                </span>
              )}
            </li>
          );
        })}
        {mutable && !showAdd && (
          <li className="bd-add">
            <button
              type="button"
              className="bd-add-btn"
              // The typed lane gets the structured form — name, scope line, the
              // must-haves it takes over — which is the deterministic
              // `add_child` op the reducer has always accepted and nothing ever
              // sent. Only an unaddressable card (older proposal, older bridge
              // image) still falls back to the free-text box, and there the
              // generic placeholder is honest: it IS a sentence for a model.
              disabled={addPieceImpossible}
              title={
                addPieceImpossible ? t("breakdown.add.impossible") : undefined
              }
              onClick={() => (ops ? setShowAdd(true) : setShowAdjust(true))}
            >
              + {t("breakdown.child.addPiece")}
            </button>
            {addPieceImpossible && (
              <p className="bd-remove-note">{t("breakdown.add.impossible")}</p>
            )}
          </li>
        )}
        {/* `mutable`, not `isReview`: the FORM had the same hole the rename box
            did — it renders off `showAdd`, which is client state, so an add
            composed when the idle timer fired stayed on screen with a live
            "Add the piece" over a dead session. */}
        {mutable && showAdd && ops && (
          <li className="bd-add">
            <AddChildDialog
              criteria={entry.criteria}
              siblings={entry.children.map((c) => ({
                title: c.title,
                coveredCriteria: c.coveredCriteria,
              }))}
              stateKey={stateKey}
              onCancel={() => setShowAdd(false)}
              onConfirm={({ title, description, criterionIndices }) => {
                // No `groundingText`: nothing leaves the ticket. The criteria
                // only change owner, which is why this costs no user-evidence
                // turn — and no model turn either.
                sendOp({
                  op: "add_child",
                  title,
                  description,
                  criterionIds: criterionIndices.map(
                    (i) => `AC${String(i + 1)}`,
                  ),
                });
                setShowAdd(false);
              }}
            />
          </li>
        )}
      </ol>

      {/* Work the user took out of scope. Shown as a REVERSIBLE decision, not
          a silent deletion: "drop EntraID" almost always means "not this
          sprint", and a one-way retirement would turn a change of mind into an
          unexplained gap nothing records. */}
      {isReview && (entry.parked?.length ?? 0) > 0 && (
        <div className="bd-parked">
          <span className="bd-parked-label">{t("breakdown.parked.label")}</span>
          <ul className="bd-parked-list">
            {(entry.parked ?? []).map((text) => (
              <li key={text}>
                <span className="bd-parked-text">{text}</span>
                {/* WHICH piece the work comes back to is a decision, so it is
                    asked, not defaulted. Restoring onto `children[0]` would be
                    the same sin the old Remove button committed: quietly
                    answering a scope question on the user's behalf. */}
                {ops && !parkDisabled && (
                  <label className="bd-parked-restore-pick">
                    <span className="bd-sr">
                      {t("breakdown.parked.restoreInto", { text })}
                    </span>
                    <select
                      value=""
                      disabled={vetoed || dead || committing}
                      aria-label={t("breakdown.parked.restoreInto", { text })}
                      onChange={(e) => {
                        const target = e.target.value;
                        if (target === "") return;
                        sendOp(
                          {
                            op: "unpark_criteria",
                            parkedTexts: [text],
                            toChildId: target,
                          },
                          // Restoring GROWS the must-have list, so it too has to
                          // be anchored to a real turn. These bytes are the
                          // ASSERTION that the user acted — a criteria move with
                          // no grounding text is refused — but they are not what
                          // lands on the log: the server renders the echoed turn
                          // itself, from the reducer's own criteria array, and
                          // refuses any row that was not previously parked.
                          //
                          // It has to. The restored row goes back to the POSITION
                          // it left from (`ParkedCriterion.parkedAtIndex`), which
                          // only the server holds — this append is the right
                          // shape for the user's eyes and the wrong order for the
                          // value being written, and the echo must match that
                          // value byte for byte or the next `propose_card`
                          // re-confirm finds nothing and downgrades the criteria.
                          buildListAnswer(AC_HEADING, [...entry.criteria, text]),
                        );
                      }}
                    >
                      <option value="">{t("breakdown.parked.restore")}</option>
                      {entry.children
                        .filter(
                          (c): c is typeof c & { childId: string } =>
                            c.childId !== undefined,
                        )
                        .map((c) => (
                          <option key={c.childId} value={c.childId}>
                            {c.title}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* A deterministic op has no agent turn to narrate it, so this is the
          only receipt a refusal gets. */}
      {card.opResult !== null && !card.opResult.ok && (
        <p className="bd-op-error" role="status">
          {/* Keyed off the machine CODE, not the server's sentence: the server
              composes English, and rendering that into a German UI is a
              regression the fallback was hiding. An unknown code (deploy skew)
              falls through to the generic line rather than showing English. */}
          {opErrorText(t, card.opResult.error)}
          {/* The reducer's `empty_child` sweep runs over every piece, so its
              generic wording can't say WHICH one without risking a lie (parking
              a shared criterion can empty a sibling instead). When THIS click's
              own data already proved it would be this row, say so by name —
              and point at the one path that does what "Don't build this" here
              could not (n > 2 only: Remove is hidden below that). */}
          {card.opResult.error === "empty_child" && emptyChildSelf !== null && (
            <>
              {" "}
              {t("breakdown.op.err.empty_child.selfHint", {
                title: emptyChildSelf.title,
              })}
              {n > 2 && (
                <> {t("breakdown.op.err.empty_child.selfHintRemove")}</>
              )}
            </>
          )}
        </p>
      )}

      {isReview && (
        <footer className="bd-foot">
          <div className="bd-trust-row">
            <span
              className={`bd-trust ${fullyCovered ? "is-ok" : "is-warn"}`}
              title={
                fullyCovered && hasParked
                  ? t("breakdown.trust.tipParked")
                  : t("breakdown.trust.tip")
              }
            >
              {fullyCovered
                ? hasParked
                  ? t("breakdown.trust.coveredParked", {
                      count: String(entry.parked?.length ?? 0),
                    })
                  : t("breakdown.trust.covered")
                : t("breakdown.trust.gap")}
            </span>
            <span className="bd-reveals">
              <button
                type="button"
                className="bd-reveal"
                onClick={() => setExpandAll((v) => !v)}
              >
                {expandAll ? t("breakdown.reveal.hide") : t("breakdown.reveal.delivers")}
              </button>
              {entry.rationale.trim() !== "" && (
                <button
                  type="button"
                  className="bd-reveal"
                  aria-expanded={showRationale}
                  onClick={() => setShowRationale((v) => !v)}
                >
                  {t("breakdown.reveal.rationale")}
                </button>
              )}
            </span>
          </div>

          {showRationale && entry.rationale.trim() !== "" && (
            <p className="bd-rationale">{uiProse(entry.rationale)}</p>
          )}

          {/* WHY every "Don't build this" is missing. The explanation existed
              but lived inside the Remove dialog, which is behind a ⋯ menu and
              gated on n > 2 — so on a two-piece applied card it was unreachable
              from anywhere, and the controls simply were not there. */}
          {parkDisabled === true && ops && !dead && (
            <p className="bd-foot-note">{t("breakdown.parkDisabled.note")}</p>
          )}

          {/* Both adjust lanes are turns the session has to answer, so this
              reads `mutable` like every other mutating control. A closed
              session answers nothing, a commit in flight is about to replace
              these rows either way — and an UNANSWERED op (`vetoed`) is the
              case this gate used to miss: it left the free-text box as the one
              live control on the card, and a sentence sent from it raced the op
              already out on the same `baseChildrenHash`. `showAdjust` is
              deliberately NOT cleared on a veto (`cardOver` excludes it), so a
              half-typed instruction comes back with the box when the answer
              lands. */}
          {!mutable ? null : showAdjust ? (
            <div className="bd-adjust">
              <AutoGrowTextarea
                className="bd-adjust-input"
                autoFocus
                placeholder={t("breakdown.adjust.describe")}
                value={adjustDraft}
                onChange={(e) => setAdjustDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && adjustDraft.trim().length > 0) {
                    e.preventDefault();
                    sendAdjust();
                  } else if (e.key === "Escape") {
                    setShowAdjust(false);
                  }
                }}
              />
              <button
                type="button"
                className="bd-btn is-ghost"
                disabled={adjustDraft.trim().length === 0}
                onClick={sendAdjust}
              >
                {t("breakdown.adjust.send")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="bd-reveal bd-adjust-open"
              onClick={() => setShowAdjust(true)}
            >
              {t("breakdown.adjust.open")}
            </button>
          )}

          <div className="bd-commit">
            {committing ? (
              // No buttons at all while the irreversible write is running. A
              // disabled "Create N sub-issues" in the same slot reads as the
              // resting state and invites the second press this replaces.
              <div className="bd-actions" aria-live="polite">
                <span className="bd-committing">{t("breakdown.committing")}</span>
              </div>
            ) : armed ? (
              <div className="bd-actions" aria-live="polite">
                {/* Cancel occupies the ORIGINAL primary position — a reflexive
                    double-click lands here, not on the irreversible write. */}
                <button
                  type="button"
                  className="bd-btn is-ghost"
                  onClick={() => setArmed(false)}
                >
                  {t("breakdown.cancel")}
                </button>
                <button
                  type="button"
                  className="bd-btn is-danger"
                  disabled={!confirmable}
                  title={commitCaption}
                  onClick={commit}
                >
                  {t("breakdown.confirmArm", { count: String(n) })}
                </button>
                <span className="bd-sr" role="status">
                  {t("breakdown.confirmArmAria", { count: String(n) })}
                </span>
              </div>
            ) : (
              <div className="bd-actions">
                <button
                  type="button"
                  className="bd-btn is-primary"
                  disabled={!confirmable}
                  title={commitCaption}
                  onClick={() => setArmed(true)}
                >
                  {t("breakdown.confirm", { count: String(n) })}
                </button>
                {/* Decline is a DECISION the host routes (see `onDecline`), not
                    a sentence this card sends — and it is dead too once the
                    session is, because whichever lane the host picks needs a
                    socket. */}
                <button
                  type="button"
                  className="bd-btn is-ghost"
                  disabled={dead}
                  title={dead ? commitCaption : undefined}
                  onClick={onDecline}
                >
                  {t("breakdown.decline")}
                </button>
              </div>
            )}
            {/* ONE caption, five states, and it can never contradict the button
                next to it — the same value is the button's `title`.
                  · dead        — the session is closed; nothing here can run.
                  · committing  — the accept frame is out and the write is live.
                  · vetoed      — this card sent a change; nothing to confirm yet.
                  · tokenPending— fully covered, no token: Loom is re-verifying
                    after the user's own edit (park/unpark strip the token trio
                    on purpose). Said OUT LOUD instead of just greying
                    the button, so "nothing happened" reads as "one moment".
                  · otherwise   — what pressing Confirm will do.
                The armed state gets its own line: a label swap alone read as
                nothing having happened, so step 1 announces itself. */}
            <p
              className={`bd-commit-caption${dead ? " bd-dead-note" : vetoed || tokenPending || committing ? " bd-token-pending" : ""}`}
              role="status"
            >
              {armed && !dead && !committing
                ? t("breakdown.confirmArmCaption")
                : commitCaption}
            </p>
          </div>
        </footer>
      )}

      {phase === "failed" && result !== null && !result.ok && (
        <footer className="bd-foot bd-failed">
          <strong className="bd-error">{t("breakdown.failed.title")}</strong>
          {/* Stale-token family: retrying won't help, so say what to do. */}
          {/(?:token_unverified|parent_not_applied|criteria_stale)/.test(result.error ?? "") && (
            <p className="bd-failed-hint">{t("breakdown.failed.stale")}</p>
          )}
          {(result.progress?.created ?? 0) > 0 && (
            <p className="bd-failed-hint">
              {t("breakdown.failed", {
                created: String(result.progress?.created ?? 0),
                total: String(result.progress?.total ?? n),
              })}
            </p>
          )}
          {/* Full transparency, one tap away: the actionable message stays
              primary; the verbatim reason lives behind a native disclosure so
              raw server machinery isn't the front-and-center dead-end (UX audit
              — hide the machinery, keep it reachable). */}
          {result.error !== undefined && result.error !== "" && (
            <details className="bd-failed-detail">
              <summary>{t("breakdown.failed.detail")}</summary>
              <code className="bd-failed-reason">
                {t("breakdown.failed.reason", { error: result.error })}
              </code>
            </details>
          )}
          {/* The RESUME leg (`failed` phase), not the commit: the split is
              already claimed, so `confirmable` — which requires `phase ===
              "review"` — is false by construction here and cannot gate it. Its
              own predicate is the one the handler needs (`acceptBreakdown`
              refuses on a missing token) and the disabled state reads it
              directly, so the two still cannot disagree. */}
          <button
            type="button"
            className="bd-btn is-primary"
            disabled={!retryable}
            title={dead || committing ? commitCaption : undefined}
            onClick={retry}
          >
            {committing ? t("breakdown.committing") : t("breakdown.retry")}
          </button>
          {/* The retry is as dead as everything else once the session is — and
              a failed split that says nothing about why the only button on
              screen is greyed is the shape being fixed everywhere here. */}
          {dead && <p className="bd-failed-hint">{commitCaption}</p>}
        </footer>
      )}
    </section>
  );
}
